import React from 'react';
import {FastField, ErrorMessage} from 'formik';
import {FormattedMessage} from 'react-intl';
import get from 'lodash/get';
import classNames from 'classnames';
import messages from '../../../res/translations/messages';
import Error from '../Error';
import FormGroupCheckbox from '../admin/FormGroupCheckbox';
import LocaleOptions from './LocaleOptions';
import RichEditorField from '../rte/RichEditorField';
import SurveyTagsField from './SurveyTagsField';
import SurveyEditorBox from './editor/SurveyEditorBox';
import TooltipIcon from '../TooltipIcon';
import DatePicker from 'react-datepicker';
import ReactDatePickerInput from '../quicksightDashboards/components/ReactDatePickerInput';
import {dayToPickerDate, pickerDateToDay, todayUTC} from '../../services/expiryAbsoluteDate';

const SurveySettingsSection = ({values, errors, touched, setFieldValue, intl}) => (
    <>
        <div className="form-group">
            <label className="col-form-label" htmlFor="sef_name">
                <FormattedMessage {...messages.surveys.form.label.surveyName} /></label>
            <FastField name="name" type="text" id="sef_name"
                className={classNames('form-control', {error: get(errors, 'name') && get(touched, 'name')})} />
            <ErrorMessage name="name">{(message) => <Error reason={message.key} reasonParams={message.params} />}</ErrorMessage>
        </div>
        <div className="form-group">
            <label className="col-form-label" htmlFor="sef_title">
                <FormattedMessage {...messages.surveys.form.label.surveyTitle} /></label>
            <FastField name="title" type="text" id="sef_title"
                className={classNames('form-control', {error: get(errors, 'title') && get(touched, 'title')})} />
            <ErrorMessage name="title">{(message) => <Error reason={message.key} reasonParams={message.params} />}</ErrorMessage>
        </div>
        <div className="form-group">
            <label className="col-form-label" htmlFor="sef_intro"><FormattedMessage {...messages.surveys.form.label.intro} /></label>
            <FastField name="intro" component={RichEditorField} id="sef_intro" hasImage showQuestions={false}
                className={classNames('form-control', {error: get(errors, 'intro') && get(touched, 'intro')})} />
            <ErrorMessage name="intro">{(message) => <Error reason={message.key} reasonParams={message.params} />}</ErrorMessage>
        </div>
        <div className="form-group">
            <label className="col-form-label" htmlFor="sef_locale"><FormattedMessage {...messages.surveys.form.label.locale} /></label>
            <FastField name="locale" component="select" id="sef_locale"
                className={classNames('form-control', {error: get(errors, 'intro') && get(touched, 'intro')})} >
                <LocaleOptions />
            </FastField>
            <ErrorMessage name="locale">{(message) => <Error reason={message.key} reasonParams={message.params} />}</ErrorMessage>
        </div>
        <div className="form-group">
            <label className="col-form-label" htmlFor="sef_published">
                <FormattedMessage {...messages.surveys.form.label.published} />
            </label>
            <FastField name="published" component="select" id="sef_published"
                className={classNames('form-control', {error: get(errors, 'published') && get(touched, 'published')})} >
                <option value={true}>{intl.formatMessage(messages.survey.published)}</option>
                <option value={false}>{intl.formatMessage(messages.survey.unpublished)}</option>
            </FastField>
            <ErrorMessage name="published">{(message) => <Error reason={message.key} reasonParams={message.params} />}</ErrorMessage>
        </div>
        <div className="form-group">
            <FormGroupCheckbox name="backEnabled" id="sef_backEnabled"
                label={messages.surveys.form.label.backEnabled} />
        </div>
        <SurveyEditorBox title={messages.surveys.form.label.expiry}>
            <div className="form-group">
                <FormGroupCheckbox name="expiryEnabled" id="sef_expiryEnabled"
                    label={messages.surveys.form.label.expiryEnabled} />
            </div>

            {values.expiryEnabled && (
                <>
                    <div className="form-group">
                        <label className="col-form-label" htmlFor="sef_expiryDays">
                            <FormattedMessage {...messages.surveys.form.label.expiryDays} />
                            {' '}<TooltipIcon message={messages.surveys.form.tooltip.expiryDays} />
                        </label>
                        <FastField
                            type="number"
                            className="form-control"
                            min={1}
                            id="sef_expiryDays"
                            name="expiryDays"
                        />
                        <ErrorMessage name="expiryDays">
                            {(message) => <Error reason={message.key} reasonParams={message.params} />}
                        </ErrorMessage>
                    </div>

                    <div className="form-group">
                        <label className="col-form-label" htmlFor="sef_expiryAbsoluteDate">
                            <FormattedMessage {...messages.surveys.form.label.expiryAbsoluteDate} />
                            {' '}<TooltipIcon message={messages.surveys.form.tooltip.expiryAbsoluteDate} />
                        </label>
                        <div className={classNames('survey-expiry-absolute-date', {error: get(errors, 'expiryAbsoluteDate')})}>
                            <DatePicker
                                id="sef_expiryAbsoluteDate"
                                selected={dayToPickerDate(values.expiryAbsoluteDate)}
                                // eslint-disable-next-line react/jsx-no-bind
                                onChange={(date) => setFieldValue('expiryAbsoluteDate', pickerDateToDay(date))}
                                minDate={dayToPickerDate(todayUTC())}
                                customInput={<ReactDatePickerInput />}
                                dateFormat="dd/MM/yyyy"
                                isClearable
                            />
                        </div>
                        <ErrorMessage name="expiryAbsoluteDate">
                            {(message) => <Error reason={message.key} reasonParams={message.params} />}
                        </ErrorMessage>
                    </div>
                </>
            )}
        </SurveyEditorBox>
        <div className="form-group">
            <label className="col-form-label">
                <FormattedMessage {...messages.surveys.form.label.tags} />
            </label>
            <SurveyTagsField
                value={values.tags || []}
                // eslint-disable-next-line react/jsx-no-bind
                onChange={(tags) => setFieldValue('tags', tags)}
            />
        </div>
    </>
);

export default SurveySettingsSection;
